import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Layouts
import MainLayout from '../components/layout/MainLayout';
import DashboardLayout from '../components/layout/DashboardLayout';
import AuthLayout from '../components/layout/AuthLayout';

// Public Pages
import Home from '../pages/public/Home';
import About from '../pages/public/About';
import Rooms from '../pages/public/Rooms';
import Gallery from '../pages/public/Gallery';
import Contact from '../pages/public/Contact';

// Auth Pages
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import ForgotPassword from '../pages/auth/ForgotPassword';

// Customer Pages
import CustomerDashboard from '../pages/customer/Dashboard';
import MyBookings from '../pages/customer/MyBookings';
import BookRoom from '../pages/customer/BookRoom';
import CustomerProfile from '../pages/customer/Profile';

// Agent Pages
import AgentDashboard from '../pages/agent/Dashboard';
import CreateBooking from '../pages/agent/CreateBooking';
import MyCommissions from '../pages/agent/MyCommissions';
import Customers from '../pages/agent/Customers';

// Owner Pages
import OwnerDashboard from '../pages/owner/Dashboard';
import ManageRooms from '../pages/owner/ManageRooms';
import AllBookings from '../pages/owner/AllBookings';
import Reports from '../pages/owner/Reports';
import ManageUsers from '../pages/owner/ManageUsers';
import ManageAgents from '../pages/owner/ManageAgents';
import ManageGallery from '../pages/owner/ManageGallery';
import ManageSiteContent from '../pages/owner/ManageSiteContent';
import ManageContacts from '../pages/owner/ManageContacts';
import ManagePermissionRoles from '../pages/owner/ManagePermissionRoles';
import ManagePortalUsers from '../pages/owner/ManagePortalUsers';
import ManageSettings from '../pages/owner/ManageSettings';
import Backup from '../pages/owner/Backup';
import PaymentReports from '../pages/owner/PaymentReports';
import CreateOfflineBooking from '../pages/owner/CreateOfflineBooking';
import CalendarView from '../pages/owner/CalendarView';
import ManageHowToReach from '../pages/owner/ManageHowToReach';

// ─── Route Guards ─────────────────────────────────────────────────────────────

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.user_type)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  if (isAuthenticated) {
    switch (user?.user_type) {
      case 'customer':
        return <Navigate to="/customer/dashboard" replace />;
      case 'agent':
        return <Navigate to="/agent/dashboard" replace />;
      case 'owner':
      case 'staff':
        return <Navigate to="/owner/dashboard" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  return children;
};

// ─── App Routes ───────────────────────────────────────────────────────────────

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route element={<MainLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/rooms" element={<Rooms />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/contact" element={<Contact />} />
      </Route>

      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Route>

      {/* Customer Routes */}
      <Route
        path="/customer"
        element={
          <ProtectedRoute allowedRoles={['customer']}>
            <DashboardLayout userType="customer" />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<CustomerDashboard />} />
        <Route path="bookings" element={<MyBookings />} />
        <Route path="book-room" element={<BookRoom />} />
        <Route path="profile" element={<CustomerProfile />} />
      </Route>

      {/* Agent Routes */}
      <Route
        path="/agent"
        element={
          <ProtectedRoute allowedRoles={['agent']}>
            <DashboardLayout userType="agent" />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<AgentDashboard />} />
        <Route path="create-booking" element={<CreateBooking />} />
        <Route path="commissions" element={<MyCommissions />} />
        <Route path="customers" element={<Customers />} />
      </Route>

      {/* Owner + Staff Routes (staff access the owner portal with permission filters) */}
      <Route
        path="/owner"
        element={
          <ProtectedRoute allowedRoles={['owner', 'staff']}>
            <DashboardLayout userType="owner" />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<OwnerDashboard />} />
        <Route path="bookings" element={<AllBookings />} />
        <Route path="rooms" element={<ManageRooms />} />
        <Route path="gallery" element={<ManageGallery />} />
        <Route path="content" element={<ManageSiteContent />} />
        <Route path="contacts" element={<ManageContacts />} />
        <Route path="reports" element={<Reports />} />
        <Route path="users" element={<ManageUsers />} />
        <Route path="agents" element={<ManageAgents />} />
        <Route path="permission-roles" element={<ManagePermissionRoles />} />
        <Route path="portal-users" element={<ManagePortalUsers />} />
        <Route path="settings" element={<ManageSettings />} />
        <Route path="backup" element={<Backup />} />
        <Route path="payments" element={<PaymentReports />} />
        <Route path="create-booking" element={<CreateOfflineBooking />} />
        <Route path="calendar" element={<CalendarView />} />
        <Route path="how-to-reach" element={<ManageHowToReach />} />
      </Route>

      {/* Fallback Routes */}
      <Route
        path="/unauthorized"
        element={
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', padding: 24, textAlign: 'center' }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 900, color: '#1f2937', margin: 0 }}>Unauthorized Access</h1>
          </div>
        }
      />
      <Route
        path="*"
        element={
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', padding: 24, textAlign: 'center' }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, fontWeight: 900, color: '#1f2937', margin: 0 }}>404 - Page Not Found</h1>
          </div>
        }
      />
    </Routes>
  );
};

export default AppRoutes;
